/**
 * How somebody's affiliation is written, everywhere on screen: `Shark-2`.
 *
 * ── Why the pair, and not the team number on its own ────────────────────────
 * Everyone here used to be "a nickname and a team number". A bare number is a
 * seat rather than a place of work: it says nothing about who a person works
 * for, and it stops identifying anybody at all the moment a second
 * organisation has a team 2 of its own. The company is the half that makes the
 * number mean something, and the two are always drawn together — which is why
 * this is a function and not two fields each screen arranges for itself.
 *
 * ⚠️ ONE DEFINITION, TWO RUNTIMES. The server has its own copy in
 * backend/src/lib/identity.ts (`companyTag`, `companyOf`, `DEFAULT_COMPANY`)
 * because it composes the same label for logs, the console and the team page's
 * title without asking the browser. They are three lines each and they must
 * agree: a header saying `Shark-2` over a console saying `Shark 2` is the kind
 * of drift nobody reports and everybody notices.
 */

/**
 * The company an account belongs to when it does not say.
 *
 * Only ever reached by an account written before the field existed — every one
 * of those belongs to this company, which is the fact backend/tools/setCompany
 * records. Kept here as well so a lookup that failed, or an older backend,
 * still renders a label rather than a hole. The registration form's pre-filled
 * value comes from the SERVER (`AuthState.defaultCompany`), not from here.
 */
export const DEFAULT_COMPANY = 'Shark';

/**
 * A company name to display — the stored one, or '' when there is none.
 *
 * No fallback any more (2026-10-06): the company became OPTIONAL at sign-up,
 * so an absent company means "did not say", not "legacy Shark account". Every
 * legacy account was migrated by tools/setCompany, so none is left to fall back.
 */
export function companyOf(company: string | null | undefined): string {
  return (company ?? '').trim();
}

/**
 * Made-up company names for the EMPTY registration box's placeholder — an
 * example of the shape, never a value. Nothing is filed under one of these.
 */
const PLACEHOLDER_COMPANIES = [
  'Bluefin Labs', 'Northwind', 'Cedar & Co', 'Orbit Studio', 'Maple Works',
  'Silverline', 'Harbor Tech', 'Lumen Group', 'Pine Systems', 'Atlas Digital',
  'Copper Fox', 'Nimbus Soft', 'Redwood Partners', 'Kite Media', 'Quarry Nine',
];

export function randomCompanyPlaceholder(): string {
  return PLACEHOLDER_COMPANIES[Math.floor(Math.random() * PLACEHOLDER_COMPANIES.length)];
}

/**
 * `Shark-2`. The one string that names where somebody sits.
 *
 * Team 0 is not special-cased. It means "not on a team", and `Shark-0` says so
 * to anybody who has read the form where the 0 button explains itself — while
 * a bare company name would make a teamless person look like a company rather
 * than like a member of one.
 *
 * A null team gives the company alone, which is what an anonymous visitor row
 * in the console needs: they have never been asked for a number.
 */
export function companyTag(
  company: string | null | undefined,
  team: number | null | undefined,
): string {
  const name = companyOf(company);
  // No company: the team alone, and nothing at all for "no team" — a bare
  // `-0` or `No team` chip would be noise for somebody who skipped both.
  if (!name) return team ? `Team ${team}` : '';
  return team == null ? name : `${name}-${team}`;
}
